import { useCallback, useEffect, useRef, useState } from "react";
import {
  moderationApi,
  ModerationApiError,
  type AuditRow,
  type CaseDetail,
  type SupportMessage,
  type TimelineEvent,
} from "../../lib/moderation-api";
import { notifyModerationRead } from "../useModerationNotifications";
import { StatusBadge, fmtDate, shortId } from "./adminShared";
import { cn } from "../cn";

/**
 * Right column — full case dossier + privileged actions. All mutations go
 * through the server authority (`require_admin` + trusted-origin + CSRF). Reads
 * expose the full evidence chain (timeline, lineage, append-only audit) that is
 * NEVER shown to the owner.
 */
export function AdminCaseDetail({ caseId, onActioned }: { caseId: string; onActioned: () => void }) {
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reqRef = useRef(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const myId = ++reqRef.current;
    const c = new AbortController();
    setDetail(null);
    setLoadErr(null);
    moderationApi
      .caseDetail(caseId, c.signal)
      .then((d) => {
        if (myId !== reqRef.current) return;
        setDetail(d);
        // Admin opened this dossier → clear the admin-side unread glow (top bar
        // + row badge). Fire-and-forget; refresh the global glow immediately.
        if (d.support_thread.some((m) => m.sender_role === "owner")) {
          moderationApi
            .markAdminRead(caseId)
            .then(() => { notifyModerationRead(); onActioned(); })
            .catch(() => {});
        }
      })
      .catch((e) => {
        if (c.signal.aborted || myId !== reqRef.current) return;
        setLoadErr(e instanceof ModerationApiError ? e.message : "Failed to load case");
      });
    return () => c.abort();
  }, [caseId, tick]);

  if (loadErr) {
    return (
      <div data-testid="admin-detail-error" className="panel-chamfer panel-chamfer--sm p-6 font-mono text-[12px] text-state-danger">
        {loadErr}
      </div>
    );
  }
  if (!detail) {
    return (
      <div data-testid="admin-detail-loading" className="panel-chamfer panel-chamfer--sm p-8 text-center font-mono text-[12px] text-text-muted">
        Loading case…
      </div>
    );
  }

  const c = detail.case;
  const active = c.status === "MODERATED" || c.status === "REVIEW_PENDING";

  const afterAction = () => {
    reload();
    onActioned();
  };

  return (
    <div data-testid="admin-case-detail" data-case-id={c.case_id} className="panel-chamfer panel-chamfer--sm">
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>Case</span>
        <span className="ml-auto"><StatusBadge status={c.status} testid="admin-detail-status" /></span>
      </div>

      <div className="p-4 space-y-4 max-h-[64vh] overflow-y-auto scroll-dark">
        {/* Identity block */}
        <dl className="space-y-1.5">
          <KV label="Target">
            <span data-testid="admin-detail-target" className="text-text-primary">
              <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted mr-2">{c.target_type}</span>
              <span className="font-mono text-[11px] break-all">{c.target_id}</span>
            </span>
          </KV>
          <KV label="Owner">
            <span data-testid="admin-detail-owner" className="font-mono text-[11px] text-text-secondary break-all">{c.owner ?? "—"}</span>
          </KV>
          <KV label="Reason">
            <span data-testid="admin-detail-reason" className="text-text-primary">{c.reason_label ?? c.reason_category}</span>
            {c.emergency && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.14em] text-neon-coral">emergency</span>}
          </KV>
          {c.admin_note && (
            <KV label="Admin Note">
              <span data-testid="admin-detail-note" className="text-text-secondary whitespace-pre-wrap break-words">{c.admin_note}</span>
            </KV>
          )}
          <KV label="Case ID"><span className="font-mono text-[10px] text-text-muted break-all">{c.case_id}</span></KV>
          <KV label="Root"><span className="font-mono text-[10px] text-text-muted break-all">{c.root_case_id ?? "—"}</span></KV>
          <KV label="Updated"><span className="font-mono text-[10px] text-text-muted">{fmtDate(c.updated_at)}</span></KV>
        </dl>

        {/* Actions */}
        {active ? (
          <CaseActions caseId={c.case_id} onActioned={afterAction} />
        ) : (
          <div data-testid="admin-case-closed" className="rounded-md border border-border-soft bg-obsidian/50 px-3 py-2.5 font-mono text-[11px] text-text-muted">
            Case closed ({c.status.toLowerCase()}). {c.restored_at ? `Restored ${fmtDate(c.restored_at)}.` : ""} No further action.
          </div>
        )}

        {/* Support thread (admin reply allowed while active) */}
        <SupportThread caseId={c.case_id} thread={detail.support_thread} canReply={active} onSent={afterAction} />

        {/* Timeline */}
        <Section title="Timeline" testid="admin-timeline">
          <ol className="space-y-1.5">
            {detail.case.timeline.map((ev, i) => <TimelineRow key={i} ev={ev} />)}
            {detail.case.timeline.length === 0 && <li className="font-mono text-[11px] text-text-muted">—</li>}
          </ol>
        </Section>

        {/* Lineage */}
        {detail.lineage.length > 1 && (
          <Section title="Lineage" testid="admin-lineage">
            <ul className="space-y-1.5">
              {detail.lineage.map((l) => (
                <li key={l.case_id} data-testid="admin-lineage-row" className="flex items-center gap-2 text-[11px]">
                  <StatusBadge status={l.status} />
                  <span className="font-mono text-[10px] text-text-muted break-all">{shortId(l.case_id, 10, 6)}</span>
                  {l.case_id === c.case_id && <span className="text-neon-teal font-mono text-[9px] uppercase">current</span>}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Audit (append-only evidence) */}
        <Section title="Audit" testid="admin-audit">
          <ul className="space-y-1.5">
            {detail.audit.map((a, i) => <AuditRowView key={i} a={a} />)}
            {detail.audit.length === 0 && <li className="font-mono text-[11px] text-text-muted">—</li>}
          </ul>
        </Section>
      </div>
    </div>
  );
}

function CaseActions({ caseId, onActioned }: { caseId: string; onActioned: () => void }) {
  const [mode, setMode] = useState<"keep" | "restore" | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (mode === "keep") {
        if (!note.trim()) { setErr("A note is required to keep this content moderated."); setBusy(false); return; }
        await moderationApi.keep(caseId, note.trim());
      } else if (mode === "restore") {
        await moderationApi.restore(caseId, note.trim() || undefined);
      }
      setNote("");
      setMode(null);
      onActioned();
    } catch (e) {
      setErr(e instanceof ModerationApiError ? e.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-case-actions" className="rounded-md border border-border-soft bg-obsidian/40 p-3">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="admin-action-keep"
          data-active={mode === "keep"}
          onClick={() => { setMode(mode === "keep" ? null : "keep"); setErr(null); }}
          className="inline-flex items-center h-8 px-3.5 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-neon-coral border border-[color:var(--neon-coral-ring)] hover:bg-[color:var(--neon-coral-soft)] transition-colors"
        >
          Keep Moderated
        </button>
        <button
          type="button"
          data-testid="admin-action-restore"
          data-active={mode === "restore"}
          onClick={() => { setMode(mode === "restore" ? null : "restore"); setErr(null); }}
          className="inline-flex items-center h-8 px-3.5 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-state-success border border-[color:rgba(88,214,141,0.4)] hover:bg-[color:rgba(88,214,141,0.1)] transition-colors"
        >
          Restore
        </button>
      </div>
      {mode && (
        <div className="mt-3">
          <label className="block font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted mb-1">
            {mode === "keep" ? "Note (required)" : "Note (optional)"}
          </label>
          <textarea
            data-testid="admin-action-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-md border border-border-soft bg-obsidian px-2.5 py-2 text-[12px] text-text-primary focus:outline-none focus:border-border-accent"
          />
          {err && <div data-testid="admin-action-error" className="mt-1.5 font-mono text-[11px] text-state-danger">{err}</div>}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              data-testid="admin-action-confirm"
              onClick={run}
              disabled={busy}
              className="inline-flex items-center h-8 px-4 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-text-primary neon-glass pill-ghost disabled:opacity-40"
            >
              {busy ? "Working…" : mode === "keep" ? "Confirm Keep" : "Confirm Restore"}
            </button>
            <button
              type="button"
              data-testid="admin-action-cancel"
              onClick={() => { setMode(null); setNote(""); setErr(null); }}
              className="inline-flex items-center h-8 px-3 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-text-muted border border-border-soft hover:text-text-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SupportThread({
  caseId,
  thread,
  canReply,
  onSent,
}: {
  caseId: string;
  thread: SupportMessage[];
  canReply: boolean;
  onSent: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setErr(null);
    try {
      await moderationApi.adminSupportReply(caseId, body);
      setDraft("");
      onSent();
    } catch (e) {
      setErr(e instanceof ModerationApiError ? e.message : "Could not send reply.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Support" testid="admin-support">
      <ul data-testid="admin-support-thread" className="space-y-2 max-h-44 overflow-y-auto scroll-dark">
        {thread.length === 0 && <li className="font-mono text-[11px] text-text-muted">No support messages.</li>}
        {thread.map((m) => (
          <li key={m.message_id} data-testid="admin-support-message" data-role={m.sender_role} className="text-[12px]">
            <span className={cn("font-mono text-[8.5px] uppercase tracking-[0.16em] mr-2", m.sender_role === "admin" ? "text-neon-teal" : "text-text-muted")}>
              {m.sender_role === "admin" ? "Admin" : "Owner"}
            </span>
            <span className="text-text-secondary whitespace-pre-wrap break-words">{m.body}</span>
          </li>
        ))}
      </ul>
      {canReply && (
        <div className="mt-2 flex items-end gap-2">
          <textarea
            data-testid="admin-support-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Reply to the owner…"
            className="flex-1 resize-none rounded-md border border-border-soft bg-obsidian px-2.5 py-2 text-[12px] text-text-primary focus:outline-none focus:border-border-accent"
          />
          <button
            type="button"
            data-testid="admin-support-send"
            onClick={send}
            disabled={busy || !draft.trim()}
            className="inline-flex items-center h-8 px-3.5 rounded-md font-mono text-[10.5px] uppercase tracking-[0.16em] text-neon-teal border border-border-accent hover:bg-neon-teal-soft disabled:opacity-40 transition-colors"
          >
            Send
          </button>
        </div>
      )}
      {err && <div data-testid="admin-support-error" className="mt-1.5 font-mono text-[11px] text-state-danger">{err}</div>}
    </Section>
  );
}

function TimelineRow({ ev }: { ev: TimelineEvent }) {
  return (
    <li data-testid="admin-timeline-row" className="flex items-start gap-2 text-[11px]">
      <span className="mt-0.5 shrink-0 rounded border border-border-soft px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.12em] text-text-muted">
        {ev.type}
      </span>
      <div className="min-w-0">
        <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-text-muted">{ev.actor_role}</span>
        <span className="ml-2 font-mono text-[9px] text-text-muted">{fmtDate(ev.at)}</span>
        {ev.reason_label && <div className="text-text-secondary">{ev.reason_label}</div>}
        {ev.note && <div className="text-text-secondary whitespace-pre-wrap break-words">{ev.note}</div>}
      </div>
    </li>
  );
}

function AuditRowView({ a }: { a: AuditRow }) {
  return (
    <li data-testid="admin-audit-row" className="flex items-start gap-2 text-[11px]">
      <span className="mt-0.5 shrink-0 rounded border border-[color:var(--spec-hairline)] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.12em] text-[color:var(--spec-cyan)]">
        {a.action}
      </span>
      <div className="min-w-0">
        <span className="font-mono text-[9px] text-text-muted break-all">{shortId(a.actor_wallet, 8, 6)}</span>
        <span className="ml-2 font-mono text-[9px] text-text-muted">{fmtDate(a.created_at)}</span>
      </div>
    </li>
  );
}

function Section({ title, testid, children }: { title: string; testid?: string; children: React.ReactNode }) {
  return (
    <div data-testid={testid}>
      <div className="mb-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">{title}</div>
      {children}
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="w-24 shrink-0 font-mono text-[8.5px] uppercase tracking-[0.18em] text-text-muted pt-0.5">{label}</dt>
      <dd className="m-0 flex-1 text-[12px]">{children}</dd>
    </div>
  );
}
